import { FC, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import axios from "../../utils/axios";
import { ApiStatus } from "../../utils/api.types";
import LocationSelect from "./pickers/LocationSelect";
import SkuSelect from "./pickers/SkuSelect";
import BatchNumberSelect from "./pickers/BatchNumberSelect";
import { useUnitName, unitOptionTokens, baseUnitNameOf, factorOf } from "./pickers/useUnitName";
import { addGRN, cancelGRN, fetchGRNById, postGRN, updateGRN, reverseGRN, fetchPurchaseInvoicesForGRN, fetchPurchaseInvoiceLines } from "../../store/grnStore/grnThunk";

const unitOptionsFor = unitOptionTokens;

const toDateInput = (d: any) => (d ? new Date(d).toISOString().slice(0, 10) : "");

const emptyRow = () => ({
  sku: null as any,
  skuId: "",
  unit: "",
  qtyReceived: "",
  batchNumber: "",
  batchLocked: false, // true when an existing batch is selected (dates are locked)
  mfgDate: "",
  expiryDate: "",
  salePrice: "",
  purchasePrice: "",
  mrp: "",
  barcode: "", // supplier's printed code, captured for manufacturer-barcoded items
});

const GRNForm: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;
  const { status } = useAppSelector((state) => state.grns);
  const unitName = useUnitName();

  const [locationId, setLocationId] = useState("");
  const [items, setItems] = useState<any[]>([emptyRow()]);
  const [docStatus, setDocStatus] = useState<string>("draft");
  const [grnNumber, setGrnNumber] = useState<string | null>(null);
  // Optional link to the purchase bill (PO). Selecting one auto-loads its
  // stock-tracked lines as GRN rows and carries supplierId + refVoucherId.
  // Options are SERVER-searched (latest 10 by default) — never the full list.
  const [piOptions, setPiOptions] = useState<any[]>([]);
  const [piSearch, setPiSearch] = useState("");
  const [refVoucher, setRefVoucher] = useState<any>(null);
  const [supplierId, setSupplierId] = useState<string>("");
  const [piLoading, setPiLoading] = useState(false);
  // Reverse lock: a posted GRN can only be reversed while all its received stock
  // is still intact. The backend tells us if any of it has been consumed (by a
  // transfer / requisition / sale) so we can disable the button proactively.
  const [canReverse, setCanReverse] = useState(false);
  const [reverseBlockReason, setReverseBlockReason] = useState<string>("");

  const readOnly = isEditing && docStatus !== "draft";

  useEffect(() => {
    if (isEditing && id) {
      dispatch(fetchGRNById(id))
        .unwrap()
        .then((grn: any) => {
          setLocationId(grn.locationId?._id || grn.locationId || "");
          setDocStatus(grn.status || "draft");
          setGrnNumber(grn.grnNumber || null);
          setCanReverse(!!grn.canReverse);
          setReverseBlockReason(grn.reverseBlockReason || "");
          setSupplierId(grn.supplierId?._id || grn.supplierId || "");
          if (grn.refVoucherId) {
            setRefVoucher({
              _id: grn.refVoucherId?._id || grn.refVoucherId,
              invoiceNumber: grn.refVoucherId?.invoiceNumber || "",
              voucherType: grn.refVoucherId?.voucherType || "",
            });
          }
          setItems(
            (grn.items || []).map((it: any) => ({
              sku: it.skuId,
              skuId: it.skuId?._id || it.skuId,
              unit: it.unit || "",
              qtyReceived: it.qtyReceived ?? "",
              batchNumber: it.batchNumber || "",
              batchLocked: false,
              mfgDate: toDateInput(it.mfgDate),
              expiryDate: toDateInput(it.expiryDate),
              salePrice: it.salePrice ?? "",
              purchasePrice: it.purchasePrice ?? "",
              mrp: it.mrp ?? "",
              barcode: it.barcode || "",
            }))
          );
        })
        .catch((e: any) => toast.error(e?.message || "Failed to load GRN"));
    }
  }, [id, isEditing, dispatch]);

  const updateRow = (idx: number, patch: any) =>
    setItems((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  // Short tag for the reference-voucher picker so a Purchase Order and a Purchase
  // Invoice (bill) are distinguishable at a glance. Empty for anything else.
  const refTag = (o: any) =>
    o?.voucherType === "Purchase Order"
      ? "PO"
      : o?.voucherType === "Purchase Invoice"
      ? "PI"
      : "";

  // Purchase-invoice options (new GRNs only): server-side search, debounced.
  // No search term -> the server returns just the latest 10.
  useEffect(() => {
    if (isEditing) return;
    const t = setTimeout(() => {
      dispatch(fetchPurchaseInvoicesForGRN(piSearch || undefined))
        .unwrap()
        .then((data: any) => setPiOptions(Array.isArray(data) ? data : []))
        .catch(() => setPiOptions([]));
    }, 350);
    return () => clearTimeout(t);
  }, [dispatch, isEditing, piSearch]);

  // Selecting a purchase invoice loads its stock-tracked lines as GRN rows
  // (qty/prices editable — partial receipts are normal) and links the bill.
  const onPickPurchaseInvoice = async (inv: any) => {
    setRefVoucher(inv || null);
    if (!inv) {
      setSupplierId("");
      return;
    }
    setPiLoading(true);
    try {
      const data = await dispatch(fetchPurchaseInvoiceLines(inv._id)).unwrap();
      const { supplierId: sup, lines } = data || {};
      setSupplierId(sup || "");
      if (Array.isArray(lines) && lines.length) {
        setItems(
          lines.map((l: any) => ({
            sku: l.sku,
            skuId: l.skuId,
            unit: l.unit || "",
            qtyReceived: l.qtyReceived || "",
            batchNumber: "",
            batchLocked: false,
            mfgDate: "",
            expiryDate: "",
            salePrice: "",
            purchasePrice: l.purchasePrice || "",
            mrp: l.mrp || "",
            barcode: "",
          }))
        );
        toast.success(`Loaded ${lines.length} item(s) from ${inv.invoiceNumber}`);
      } else {
        toast.info("This purchase invoice has no stock-tracked items to receive");
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to load invoice items");
    } finally {
      setPiLoading(false);
    }
  };

  // Prefill the item's existing barcode into the row so the user can cross-check it
  // against the physical label (scanner not needed).
  const prefillExistingBarcode = async (idx: number, skuId: string) => {
    if (!skuId) return;
    try {
      const res = await axios.get(`/api/v1/erp/barcode/mappings?skuId=${skuId}`);
      const codes = (Array.isArray(res.data) ? res.data : []).filter((m: any) => m.isActive);
      const primary = codes.find((m: any) => m.isPrimary) || codes[0];
      if (primary) updateRow(idx, { barcode: primary.code });
    } catch {
      /* no existing barcode — leave the field empty */
    }
  };

  const onSkuChange = (idx: number, sku: any) => {
    const units = unitOptionsFor(sku);
    updateRow(idx, { sku, skuId: sku?._id || "", unit: units[0] || "", batchNumber: "", batchLocked: false, mfgDate: "", expiryDate: "", barcode: "" });
  };

  // Called when the user picks (or types) a batch.
  const onBatchSelect = (idx: number, batch: any) => {
    if (!batch) {
      updateRow(idx, { batchNumber: "", batchLocked: false, mfgDate: "", expiryDate: "" });
    } else if (batch.isNew) {
      // New lot → keep dates editable.
      updateRow(idx, { batchNumber: batch.batchNumber, batchLocked: false });
    } else {
      // Existing lot → auto-fill and lock dates so they can't be changed.
      updateRow(idx, {
        batchNumber: batch.batchNumber,
        batchLocked: true,
        mfgDate: batch.mfgDate || "",
        expiryDate: batch.expiryDate || "",
      });
    }
  };

  const buildPayload = () => ({
    locationId,
    refVoucherId: refVoucher?._id || null,
    supplierId: supplierId || null,
    items: items
      .filter((r) => r.skuId && Number(r.qtyReceived) > 0)
      .map((r) => ({
        skuId: r.skuId,
        qtyReceived: Number(r.qtyReceived),
        unit: r.unit || null,
        batchNumber: r.batchNumber || null,
        mfgDate: r.mfgDate || null,
        expiryDate: r.expiryDate || null,
        salePrice: Number(r.salePrice) || 0,
        purchasePrice: Number(r.purchasePrice) || 0,
        mrp: Number(r.mrp) || 0,
        barcode: r.barcode ? String(r.barcode).trim() : null,
      })),
  });

  const onSave = async () => {
    if (!locationId) return toast.error("Select a location");
    const payload = buildPayload();
    if (!payload.items.length) return toast.error("Add at least one item with a quantity");
    try {
      if (isEditing && id) {
        await dispatch(updateGRN({ id, payload })).unwrap();
        navigate("/inventory/grn");
      } else {
        const created = await dispatch(addGRN(payload)).unwrap();
        navigate(`/inventory/grn/${created._id}`);
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to save GRN");
    }
  };

  const onPost = async () => {
    if (!id) return;
    try {
      await dispatch(postGRN(id)).unwrap();
      navigate("/inventory/grn");
    } catch (e: any) {
      toast.error(e?.message || "Failed to post GRN");
    }
  };

  const onCancel = async () => {
    if (!id) return;
    try {
      await dispatch(cancelGRN(id)).unwrap();
      navigate("/inventory/grn");
    } catch (e: any) {
      toast.error(e?.message || "Failed to cancel GRN");
    }
  };

  const onReverse = async () => {
    if (!id) return;
    if (!window.confirm("Reverse this posted GRN? The received stock will be removed via a compensating entry (the original stays for audit).")) return;
    try {
      await dispatch(reverseGRN(id)).unwrap();
      navigate("/inventory/grn");
    } catch (e: any) {
      toast.error(e?.message || "Failed to reverse GRN");
    }
  };

  const loading = status === ApiStatus.LOADING;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" onClick={() => navigate("/inventory/grn")}>
            <NavigateBeforeOutlinedIcon />
          </IconButton>
          <Typography variant="h6">
            {isEditing ? `Goods Receipt${grnNumber ? ` ( ${grnNumber} )` : ""}` : "New Goods Receipt"}
          </Typography>
          {isEditing && <Chip size="small" label={docStatus ? docStatus.charAt(0).toUpperCase() + docStatus.slice(1) : ""} />}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {!readOnly && (
            <LoadingButton variant="contained" loading={loading} onClick={onSave}>
              {isEditing ? "Save" : "Create"}
            </LoadingButton>
          )}
          {isEditing && docStatus === "draft" && (
            <>
              <LoadingButton color="success" variant="contained" loading={loading} onClick={onPost}>
                Post
              </LoadingButton>
              <Button color="error" variant="outlined" onClick={onCancel}>
                Cancel GRN
              </Button>
            </>
          )}
          {isEditing && docStatus === "posted" && (
            <Tooltip title={!canReverse ? reverseBlockReason || "This GRN cannot be reversed." : ""}>
              {/* span wrapper so the tooltip still shows while the button is disabled */}
              <span>
                <LoadingButton
                  color="error"
                  variant="outlined"
                  loading={loading}
                  disabled={!canReverse}
                  onClick={onReverse}
                >
                  Reverse
                </LoadingButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Accordion
        defaultExpanded={false}
        disableGutters
        elevation={0}
        sx={{ mb: 2, border: "1px solid #e6e8ec", borderRadius: 2, "&:before": { display: "none" } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ display: "flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}>
            <InfoOutlinedIcon sx={{ fontSize: 18 }} /> How to record a Goods Receipt
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ol style={{ margin: 0, paddingLeft: 18, color: "#3c4350", lineHeight: 1.7 }}>
            <li>Pick the <b>location</b> you're receiving stock into.</li>
            <li>For each item: select the <b>item</b>, the <b>unit you're receiving in</b> (box, strip, bottle…), and the <b>quantity</b>.</li>
            <li>For batch-tracked items, enter the <b>Batch no.</b> and <b>Expiry date</b> — a batch is created when you post.</li>
            <li><b>Purchase price / unit</b> = what you paid for <b>one</b> of that unit (e.g. per box) — this drives stock value. <b>Sale price / unit</b> and <b>MRP</b> are what you sell at / print on the label.</li>
            <li>Click <b>Create</b> to save the draft, then <b>Post</b> — posting is what actually adds the stock.</li>
            {isEditing && docStatus === "posted" && (
              <li><b>Reverse</b> removes the received stock via a compensating entry — the original GRN stays for audit. Use it to undo a posted receipt.</li>
            )}
          </ol>
          <Typography sx={{ fontSize: 12, color: "#5b6472", mt: 1 }}>
            Stock is stored in each item's base unit. The grey line under an item shows how much base quantity will be added and the per-unit cost.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={5}>
          <LocationSelect value={locationId} onChange={setLocationId} label="Receive at location" />
        </Grid>
        <Grid item xs={12} sm={4}>
          {/* Optional PO link: picking a bill auto-loads its stock-tracked lines. */}
          {readOnly || isEditing ? (
            refVoucher ? (
              <TextField
                fullWidth
                size="small"
                label={refTag(refVoucher) === "PO" ? "Purchase Order" : "Purchase Invoice"}
                value={`${refTag(refVoucher) ? `[${refTag(refVoucher)}] ` : ""}${refVoucher.invoiceNumber || ""}`}
                InputProps={{ readOnly: true }}
              />
            ) : null
          ) : (
            <Autocomplete
              options={piOptions}
              value={refVoucher}
              loading={piLoading}
              // Server-side search: options are already filtered by the API
              // (latest 15 when empty) — do NOT re-filter on the client.
              filterOptions={(x) => x}
              onInputChange={(_e, val, reason) => {
                if (reason === "input") setPiSearch(val);
              }}
              getOptionLabel={(o: any) =>
                o
                  ? `${refTag(o) ? `[${refTag(o)}] ` : ""}${o.invoiceNumber}${o.supplier?.name ? ` · ${o.supplier.name}` : ""}`
                  : ""
              }
              isOptionEqualToValue={(o: any, v: any) => o._id === v._id}
              onChange={(_e, v: any) => onPickPurchaseInvoice(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Purchase Order / Invoice (optional)"
                  helperText="Type PO/invoice no. to search · latest 15 shown · leave blank for a direct receipt"
                />
              )}
            />
          )}
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
          Items received
          <Tooltip title="Enter the unit you're receiving in (box / strip / bottle…), the quantity, and the cost per ONE of that unit. Stock is stored in the item's base unit.">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#7a8290" }} />
          </Tooltip>
        </Typography>
        {items.map((row, idx) => {
          const factor = factorOf(row.sku, row.unit);
          const baseName = baseUnitNameOf(row.sku, unitName);
          const qtyNum = Number(row.qtyReceived);
          const costNum = Number(row.purchasePrice); // valuation is purchase-price based
          const baseQty = factor > 0 && qtyNum > 0 ? factor * qtyNum : 0;
          const perBase = factor > 0 && costNum > 0 ? costNum / factor : 0;
          return (
            <Box key={idx} sx={{ mb: 1.5, pb: 1, borderBottom: "1px dashed #e6e8ec" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
              <Grid container spacing={1} sx={{ alignItems: "center", flex: 1, minWidth: 0 }}>
                <Grid item xs={12} sm={2.0}>
                  <SkuSelect value={row.skuId} onChange={(sku) => onSkuChange(idx, sku)} barcodeSearch />
                </Grid>
                <Grid item xs={6} sm={1.3}>
                  <TextField select fullWidth size="small" label="Receiving unit" value={row.unit} onChange={(e) => updateRow(idx, { unit: e.target.value })} disabled={readOnly}>
                    {unitOptionsFor(row.sku).map((u) => (
                      <MenuItem key={u} value={u}>{unitName(u)}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={1.0}>
                  <TextField fullWidth size="small" type="number" label="Qty received" value={row.qtyReceived} onChange={(e) => updateRow(idx, { qtyReceived: e.target.value })} disabled={readOnly} />
                </Grid>
                <Grid item xs={12} sm={1.5}>
                  {/* Batch picker: select existing lot (auto-fills + locks dates)
                      or type a new number to create a fresh lot */}
                  {row.sku?.isBatchTracked !== false && !readOnly ? (
                    <BatchNumberSelect
                      skuId={row.skuId}
                      value={row.batchNumber}
                      onSelect={(b) => onBatchSelect(idx, b)}
                    />
                  ) : (
                    <TextField fullWidth size="small" label="Batch no." value={row.batchNumber} disabled />
                  )}
                </Grid>
                <Grid item xs={6} sm={1.4}>
                  {/* Mfg date: locked when an existing batch is chosen */}
                  <TextField fullWidth size="small" type="date" label="Mfg date" InputLabelProps={{ shrink: true }} value={row.mfgDate} onChange={(e) => updateRow(idx, { mfgDate: e.target.value })} disabled={readOnly || row.batchLocked} />
                </Grid>
                <Grid item xs={6} sm={1.4}>
                  {/* Expiry date: locked when an existing batch is chosen */}
                  <TextField fullWidth size="small" type="date" label="Expiry date" InputLabelProps={{ shrink: true }} value={row.expiryDate} onChange={(e) => updateRow(idx, { expiryDate: e.target.value })} disabled={readOnly || row.batchLocked} />
                </Grid>
                <Grid item xs={6} sm={1.0}>
                  <TextField fullWidth size="small" type="number" label="MRP (₹)" placeholder="per unit" value={row.mrp} onChange={(e) => updateRow(idx, { mrp: e.target.value })} disabled={readOnly} />
                </Grid>
                <Grid item xs={6} sm={1.1}>
                  <TextField fullWidth size="small" type="number" label="Sale Price / unit (₹)" placeholder={row.unit ? `per ${unitName(row.unit)}` : "per unit"} value={row.salePrice} onChange={(e) => updateRow(idx, { salePrice: e.target.value })} disabled={readOnly} />
                </Grid>
                <Grid item xs={6} sm={1.1}>
                  <TextField fullWidth size="small" type="number" label="Purchase Price / unit (₹)" placeholder={row.unit ? `per ${unitName(row.unit)}` : "per unit"} value={row.purchasePrice} onChange={(e) => updateRow(idx, { purchasePrice: e.target.value })} disabled={readOnly} />
                </Grid>
              </Grid>
              {!readOnly && (
                <IconButton size="small" sx={{ flexShrink: 0 }} onClick={() => setItems((rows) => rows.filter((_, i) => i !== idx))}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
              </Box>
              {/* Barcode capture — only for manufacturer-barcoded items (scan supplier code). */}
              {!readOnly && row.sku?.barcodeMode === "manufacturer" && (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    size="small"
                    label="Barcode (scan printed code)"
                    placeholder="Scan the supplier's barcode"
                    value={row.barcode}
                    onChange={(e) => updateRow(idx, { barcode: e.target.value })}
                    sx={{ width: 340 }}
                  />
                  <Button size="small" variant="outlined" onClick={() => prefillExistingBarcode(idx, row.skuId)} disabled={!row.skuId}>
                    Get existing
                  </Button>
                </Box>
              )}
              {row.sku?.barcodeMode === "internal_batch" && (
                <Typography sx={{ fontSize: 12, color: "#5b6472", mt: 0.5 }}>
                  A batch barcode label will be generated automatically when you post.
                </Typography>
              )}
              {factor > 0 && (qtyNum > 0 || costNum > 0) && (
                <Box sx={{ mt: 0.5 }}>
                  {qtyNum > 0 && (
                    <Typography sx={{ fontSize: 12, color: "#5b6472" }}>
                      Total: {qtyNum} {unitName(row.unit)} × {factor} {baseName} ={" "}
                      <b>{baseQty.toLocaleString()} {baseName}</b>
                    </Typography>
                  )}
                  {costNum > 0 && (
                    <Typography sx={{ fontSize: 12, color: "#5b6472" }}>
                      Purchase cost: ₹{costNum} ÷ {factor} = <b>₹{perBase.toFixed(2)} / {baseName}</b>
                      {qtyNum > 0 ? ` · value ₹${(perBase * baseQty).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : ""}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
        {!readOnly && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setItems((rows) => [...rows, emptyRow()])}
            sx={{ color: "#488dff", backgroundColor: "#fff", border: "0.5px solid #488dff", borderRadius: "7px", textTransform: "none", "&:hover": { backgroundColor: "#f5f9ff", border: "0.5px solid #488dff" } }}>
            Add item
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default GRNForm;
